'use client'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Mail, Lock, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import AuthHeader from '@/components/auth/AuthHeader'
import { VerificationSuccessModal } from '@/components/auth/verification-success-modal'
import { useLogin } from '@/hooks/use-auth'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

export default function Login() {
  const schema = z.object({
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters long'),
    rememberMe: z.boolean().optional().default(false),
  })

  type FormValues = {
    email: string;
    password: string;
    rememberMe?: boolean;
  }

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  })
  const loginMutation = useLogin()

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    loginMutation.mutate({
      email: data.email.trim(), // Trim spaces from email
      password: data.password, // Don't trim password - spaces might be intentional
      remember_me: Boolean(data.rememberMe),
    })
  }

  return (
    <>
      <VerificationSuccessModal />
      {/* Header */}
      <AuthHeader badge='Professional Educational Platform' />

      {/* Login Card */}
      <Card className='shadow-elegant border-border/50'>
        <CardHeader className='space-y-1 text-center'>
          <CardTitle className='text-2xl font-bold text-foreground'>
            Welcome Back
          </CardTitle>
          <CardDescription className='text-muted-foreground'>
            Sign in to your educator account to continue managing your
            educational data
          </CardDescription>
        </CardHeader>

        <CardContent className='space-y-6'>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            {/* Email Input */}
            <Input
              id='email'
              // type='email'
              placeholder='educator@school.edu'
              label='Email Address'
              startIcon={<Mail className='h-4 w-4' />}
              className={errors.email ? 'border-destructive' : ''}
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className='text-sm text-destructive'>{errors.email.message}</p>
            )}

            {/* Password Input */}
            <Input
              id='password'
              type='password'
              placeholder='Enter your password'
              label='Password'
              startIcon={<Lock className='h-4 w-4' />}
              withPasswordToggle
              className={errors.password ? 'border-destructive' : ''}
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            {errors.password && (
              <p className='text-sm text-destructive'>{errors.password.message}</p>
            )}

            {/* Remember Me & Forgot Password */}
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-2'>
                <Controller
                  name='rememberMe'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox
                      id='remember'
                      checked={Boolean(value)}
                      onCheckedChange={(checked) => onChange(Boolean(checked))}
                    />
                  )}
                />
                <Label
                  htmlFor='remember'
                  className='text-sm text-muted-foreground cursor-pointer'
                >
                  Remember me
                </Label>
              </div>
              <Link
                href='/forgot-password'
                className='text-sm text-primary hover:text-primary/80 transition-colors font-medium'
              >
                Forgot password?
              </Link>
            </div>

            {/* Login Button */}
            <Button
              type='submit'
              variant='professional'
              size='lg'
              className='w-full group'
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <div className='flex items-center'>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                  Signing in...
                </div>
              ) : (
                <>
                  Sign In to Dashboard
                  <ArrowRight className='ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform' />
                </>
              )}
            </Button>
          </form>
          {/* Divider */}
          {/*
          <div className='relative'>
            <div className='absolute inset-0 flex items-center'>
              <span className='w-full border-t border-border' />
            </div>
            <div className='relative flex justify-center text-xs uppercase'>
              <span className='bg-card px-2 text-muted-foreground'>
                Or continue with
              </span>
            </div>
          </div>
          */}
          {/* Social Login Options */}
          {/*
          <div className='grid grid-cols-2 gap-3'>
            <Button variant='outline' size='lg'>
              <svg className='mr-2 h-4 w-4' viewBox='0 0 24 24'>
                <path
                  fill='currentColor'
                  d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                />
                <path
                  fill='currentColor'
                  d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                />
                <path
                  fill='currentColor'
                  d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                />
                <path
                  fill='currentColor'
                  d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                />
              </svg>
              Google
            </Button>
            <Button variant='outline' size='lg'>
              <svg
                className='mr-2 h-4 w-4'
                fill='currentColor'
                viewBox='0 0 24 24'
              >
                <path d='M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.024-.105-.949-.199-2.403.041-3.439.219-.937 1.219-5.653 1.219-5.653s-.313-.219-.313-.219c0-1.186.687-2.072 1.543-2.072.729 0 1.081.219 1.081 1.186 0 .729-.469 1.831-.719 2.84-.209.875.438 1.593 1.313 1.593 1.575 0 2.786-1.656 2.786-4.047 0-2.115-1.521-3.594-3.695-3.594-2.52 0-4 1.891-4 3.845 0 .76.292 1.575.656 2.019.072.088.082.165.061.254-.067.281-.218 1.11-.247 1.267-.039.188-.127.229-.294.138-1.022-.475-1.663-1.962-1.663-3.158 0-2.786 2.024-5.344 5.834-5.344 3.064 0 5.448 2.181 5.448 5.102 0 3.043-1.916 5.49-4.578 5.49-.894 0-1.736-.465-2.024-1.021 0 0-.444 1.693-.552 2.103-.2.769-.741 1.731-1.103 2.315.832.257 1.71.394 2.621.394C18.638 24.001 24.005 18.634 24.005 12.014 24.005 5.393 18.638.026 12.017.026z' />
              </svg>
              Microsoft
            </Button>
          </div>
          */}
          {/* Sign Up Link */}
          <div className='text-center pt-4 border-t border-border'>
            <p className='text-sm text-muted-foreground'>
              Don't have an account?{' '}
              <Link
                href='/signup'
                className='font-medium text-primary hover:text-primary/80 transition-colors'
              >
                Create your educator account
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className='text-center mt-8'>
        <p className='text-xs text-muted-foreground'>
          By signing in, you agree to our{' '}
          <a
            href='/terms'
            className='text-primary hover:text-primary/80 transition-colors'
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href='/privacy'
            className='text-primary hover:text-primary/80 transition-colors'
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </>
  )
}

